import React, { Suspense, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { green, lightGray, lightGrayBackground } from 'utils/style/colors';
import { zindex } from 'utils/style/zindex';
import coreTranslations from '@rastech-inc/ckeditor/build/translations/en';
import { baseURL } from 'api/ManApi';
import i18n, { languageDirection } from '../../i18n';

const CKEditor = React.lazy(() =>
  import('@ckeditor/ckeditor5-react').then((module) => ({
    default: module.CKEditor,
  }))
);
import ClassicEditor from '@rastech-inc/ckeditor';
// const ClassicEditor = React.lazy(() =>
//   import('@ckeditor/ckeditor5-react').then((module) => module.default)
// );
const mediaEmbed = {
  previewsInData: true,
  providers: [
    {
      name: 'video',
      // A URL regexp or an array of URL regexps:
      url: /https?:\/\/.*\.(?:mp4|mov|flv)(?:\?.*)?$/,
      // url: /.*/,
      // url: /.*/,

      // To be defined only if the media are previewable:
      html: (match: any) =>
        `<video controls="" style="width: 100%;"><source src="${match[0]}" type="video/mp4">Your browser does not support this file</video>`,
    },
    {
      name: 'audio',
      // A URL regexp or an array of URL regexps:
      url: /https?:\/\/.*\.(?:mp3|wav|flac|aac|ogg|wma)(?:\?.*)?$/,
      // url: /.*/,

      // To be defined only if the media are previewable:
      html: (match: any) =>
        `<audio controls style="width: 90%; margin: 20px 50px; display:flex ;"><source src="${match[0]}">Your browser does not support this file</audio>`,
    },
  ],
};

export const CkEditorBaseConfig = (token?: string | null) => {
  let ret: any = {
    link: {
      // Automatically add target="_blank" and rel="noopener noreferrer" to all external links.
      addTargetToExternalLinks: true,
    },
    translations: [coreTranslations],
    mediaEmbed: mediaEmbed,
    toolbar: {
      items: [
        'heading',
        'fontColor',
        'alignment',
        '|',
        'bold',
        'italic',
        'strikethrough',
        'bulletedList',
        'numberedList',
        'todoList',
        '|',
        'lineHeight',
        'fontSize',
        '|',
        'outdent',
        'indent',
        '|',
        'horizontalLine',
        token ? 'imageUpload' : '',
        'mediaEmbed',
        'insertTable',
        'link',
        '|',
        'undo',
        'redo',
        'highlight',
        '|',
        'speech',
        'language-toggle',
        // 'suggest-toggle',
      ],
    },
    balloonToolbar: [
      'paragraph',
      'heading1',
      'heading2',
      'heading3',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'blockQuote',
      'uploadImage',
    ],
    language: i18n.language,
    image: {
      toolbar: [
        'imageStyle:alignLeft',
        'imageStyle:alignCenter',
        'imageStyle:alignRight',
        'imageStyle:full',
        '|',
        'resizeImage',
        '|',
        'imageTextAlternative',
        'linkImage',
      ],
      styles: ['full', 'alignLeft', 'alignCenter', 'alignRight'],
    },
    table: {
      contentToolbar: [
        'tableColumn',
        'tableRow',
        'mergeTableCells',
        'tableCellProperties',
        'tableProperties',
      ],
    },
  };
  if (token) {
    ret = {
      ...ret,
      simpleUpload: {
        // The URL that the images are uploaded to.
        uploadUrl: baseURL + '/file/addTempImage',

        // Enable the XMLHttpRequest.withCredentials property.
        withCredentials: false,

        // Headers sent along with the XMLHttpRequest to the upload server.
        headers: {
          Authorization: token,
          'Access-Control-Request-Headers': '',
          accept: 'application/json',
        },
      },
    };
  }
  ret.toolbar.items = ret.toolbar.items.filter((it: string) => it !== '');
  return ret;
};

export const CKEditorReadOnlyConfig = {
  link: {
    addTargetToExternalLinks: true,
  },
  toolbar: {
    items: [],
  },
  mediaEmbed: mediaEmbed,
  language: i18n.language,
};
interface IDateSpan {
  disabled: false;
}
export const StyledDiv = styled('div')<IDateSpan>`
  border: ${(props) => (props.disabled ? lightGrayBackground : lightGray)};
  transition: 0.3s all ease;
  border-radius: 10px;
  background-color: ${(props) =>
    props.disabled ? lightGrayBackground : 'var(--exact-white)'};
  box-sizing: border-box;
  min-height: 3rem;
  resize: vertical;
  width: 100%;
  height: inherit;
  outline: none;
  ${(props) => {
    return (
      props.disabled && '& > * > .ck-editor__top {  \n    display: none;  \n  }'
    );
  }}
  &:hover {
    border-color: ${(props) => (props.disabled ? undefined : green)};
  }
  &:focus {
    border-color: ${green};
    box-shadow: 0 0 0 2px rgba(50, 200, 182, 0.3);
  }
  &::placeholder {
    font-weight: bolder;
  }

  & p {
    text-align: ${languageDirection() === 'rtl' ? 'right' : 'left'};
  }

  &.ck-editor-has-open-ui {
    position: relative;
    z-index: ${zindex.portal + 1};
    overflow: visible;
  }
`;

const CK_EDITOR_OPEN_UI_SELECTOR =
  '.ck-dropdown__panel-visible, .ck-balloon-panel_visible, .ck-splitbutton_open';

const CKEditorField: React.FC<any> = ({
  id,
  label,
  wrapperClassName,
  wrapperStyle,
  key,
  disabled,
  onReady,
  onChange,
  ...ckeditorProps
}) => {
  const editorWrapperRef = useRef<HTMLDivElement>(null);

  const handleEditorReady = useCallback(
    (editor: any) => {
      onReady?.(editor);

      const editorEl = editor.ui.view.element as HTMLElement;
      const wrapper = editorWrapperRef.current;
      if (!wrapper) return;

      const syncOpenUiState = () => {
        const hasOpenUi = !!editorEl.querySelector(CK_EDITOR_OPEN_UI_SELECTOR);
        wrapper.classList.toggle('ck-editor-has-open-ui', hasOpenUi);
      };

      const observer = new MutationObserver(syncOpenUiState);
      observer.observe(editorEl, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      syncOpenUiState();

      editor.on('destroy', () => observer.disconnect());
    },
    [onReady]
  );

  return (
    <div key={key} className={wrapperClassName} style={wrapperStyle}>
      {label && <label htmlFor={id}>{label}</label>}
      <StyledDiv ref={editorWrapperRef} disabled={disabled}>
        <Suspense fallback={<div>...</div>}>
          <CKEditor
            id={id}
            {...ckeditorProps}
            editor={ClassicEditor}
            disabled={disabled}
            onReady={handleEditorReady}
            onChange={(_: any, editor: any) => {
              if (onChange) {
                const data: string = editor.getData() as string;
                onChange(data, editor);
              }
            }}
            style={{
              '--ck-focus-ring': '0px',
              '--ck-border-radius': '10px',
            }}
          />
        </Suspense>
      </StyledDiv>
    </div>
  );
};

export default React.memo(CKEditorField);
